import * as Y from "yjs";
import { storyJsonToYDoc, writeStoryToDoc, type StorydocJson } from "./schema";
import { LOCAL_ORIGIN } from "./origins";

type Fetcher = (url: string) => Promise<StorydocJson>;

const FETCH_TIMEOUT_MS = 10_000;

const defaultFetcher: Fetcher = async (url) => {
  // The seed gates the editor's loading state — a hung GCS fetch must fail
  // loudly (and let the caller fall back) rather than hang the editor forever.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch contentPointer ${url}: ${response.status}`);
    }
    return (await response.json()) as StorydocJson;
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw new Error(`Fetch timed out for contentPointer ${url}`);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

export const seedYDocFromGcs = async (
  doc: Y.Doc,
  contentPointer: string | null,
  options: {
    fetcher?: Fetcher;
    shouldApply?: () => boolean;
    // When true, REPLACE the doc's content with the fetched snapshot (clears the
    // story + entities maps first) instead of merging on top. Use when the
    // durable snapshot is authoritative and the doc may hold stale state that
    // must be dropped (e.g. a Liveblocks server doc that fell behind). A plain
    // merge would leave entities deleted in the durable version alive.
    replace?: boolean;
  } = {},
) => {
  if (!contentPointer) return;
  const fetcher = options.fetcher ?? defaultFetcher;
  const json = await fetcher(contentPointer);
  // Re-check the caller's guard after the async gap: the doc may have
  // received live state from a peer while the fetch was in flight, and the
  // (older) GCS seed must not overwrite it.
  if (options.shouldApply && !options.shouldApply()) return;
  doc.transact(() => {
    if (options.replace) {
      writeStoryToDoc(doc, json);
    } else {
      const seedDoc = storyJsonToYDoc(json);
      Y.applyUpdate(doc, Y.encodeStateAsUpdate(seedDoc));
    }
  }, LOCAL_ORIGIN);
};
